import { Box, Grid, Stack } from "@mui/material";
import { useMemo } from "react";
import { DeviceTrackerCard } from "../components/DeviceTrackerCard";
import { PersonalInformation } from "../components/PersonalInformation";
import { ProfileHeader } from "../components/ProfileHeader";
import { QuickStats } from "../components/QuickStats";
import { useGetRateWorkforce } from "../components/hooks/useWorkForce";

const EMPTY_LOCATION = { lat: 0, lng: 0 };
const DEFAULT_COVERAGE_RADIUS = 100;
const DASH = "—";

const formatJoinDate = (dateString) => {
  if (!dateString) return DASH;

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return DASH;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getAvailabilityCount = (availability) => {
  if (!availability) return 0;

  return Object.values(availability).reduce((total, period) => {
    const activeDays = Object.values(period || {}).filter(Boolean).length;
    return total + activeDays;
  }, 0);
};

const mapServices = (services = []) => services.map((service) => ({ services: service }));

const getWorkforceServicesByType = (workTypeServices = [], typeName) =>
  workTypeServices.filter((item) => item.workTypes?.name === typeName).map((item) => ({ services: item.workServices?.name }));

export const WorkforcePersonalInfo = ({ workforce, selectedRole, onRoleChange }) => {
  const workforceUid = workforce?.uid;
  const coverageArea = workforce?.coverageAreas?.[0];

  const { data: rateData, isLoading } = useGetRateWorkforce(workforceUid);

  const totalServicesCount = useMemo(() => {
    if (isLoading) return 0;
    return rateData?.assignedCount ?? workforce?.workTypeServices?.length ?? 0;
  }, [isLoading, rateData?.assignedCount, workforce?.workTypeServices?.length]);

  const completionRate = useMemo(() => {
    if (isLoading) return 0;
    return parseFloat(rateData?.completionRate ?? 0);
  }, [isLoading, rateData?.completionRate]);

  const coverageRadius = useMemo(() => {
    return parseFloat(coverageArea?.milesOfCoverageArea) || DEFAULT_COVERAGE_RADIUS;
  }, [coverageArea?.milesOfCoverageArea]);

  const location = useMemo(() => {
    const lat = parseFloat(coverageArea?.latitude ?? "0");
    const lng = parseFloat(coverageArea?.longitude ?? "0");

    return {
      lat: Number.isNaN(lat) ? EMPTY_LOCATION.lat : lat,
      lng: Number.isNaN(lng) ? EMPTY_LOCATION.lng : lng,
    };
  }, [coverageArea?.latitude, coverageArea?.longitude]);

  const availabilityCount = useMemo(() => getAvailabilityCount(workforce?.availability), [workforce?.availability]);

  const memberSince = useMemo(() => formatJoinDate(workforce?.createdAt), [workforce?.createdAt]);

  if (!workforce) return null;

  return (
    <Box sx={{ px: 3, pb: 2, maxWidth: 1400, mx: "auto" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={4}>
          <ProfileHeader workforce={workforce} selectedRole={selectedRole} onRoleChange={onRoleChange} />

          <QuickStats
            sx={{ mt: 3 }}
            completionRate={completionRate}
            availabilityCount={availabilityCount}
            servicesCount={totalServicesCount}
            memberSince={memberSince}
          />
        </Grid>

        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            <PersonalInformation workforce={workforce} />

            <DeviceTrackerCard
              coverageRadius={Math.round(coverageRadius)}
              location={location}
              address={coverageArea?.address}
              workforce={workforce}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} sx={{ mt: 1 }}>
          {/* <ServicesCard
            noServices={residentialServices.length === 0 && commercialServices.length === 0}
            residentialServices={residentialServices}
            commercialServices={commercialServices}
          /> */}
        </Grid>
      </Grid>
    </Box>
  );
};
